import { AuthChannelType } from '@hotelplan/fdr.lib.ident.auth-config';
import { useAuthentication } from '@hotelplan/fdr.lib.ident.with-auth';
import { TGeoObjectType } from 'components/domain/fdr-geo/fdr-geo-object.utils';
import { TFdrThGeoModuleObject } from 'components/domain/fdr-geo/fdr-geo.types';
import {
  getFdrGeoAdventureTravels,
  getFdrGeoChildren,
  getFdrGeoMapProducts,
  isGeoProductGalleryAvailable,
} from 'components/domain/fdr-geo/fdr-geo.utils';
import { FdrPageComponentsFragment } from 'schemas/fragment/page-components/fdr-page-components.generated';
import { FdrGeoContentQuery } from 'schemas/query/geo/fdr-geo-content.generated';
import { NavigationIds, TNavigationItem } from './fdr-geo-navigation.types';

const getNavigationList = (
  isB2b: boolean,
  geoObjectType: TGeoObjectType
): TNavigationItem[] => {
  const commonNavigationList = [
    {
      title: geoObjectType.isCountry
        ? 'geo:navigation.countryImages'
        : 'geo:navigation.regionImages',
      navId: NavigationIds.HIGHLIGHTS,
    },
    {
      title: geoObjectType.isCountry
        ? 'geo:navigation.countryImages'
        : 'geo:navigation.regionImages',
      navId: NavigationIds.MEDIA,
    },
    {
      title: 'geo:navigation.bestTravelPeriod',
      navId: NavigationIds.BEST_TRAVEL_PERIOD,
    },
    {
      title: 'geo:navigation.themes',
      navId: NavigationIds.THEMES,
    },
    {
      title: 'geo:navigation.articles',
      navId: NavigationIds.ARTICLES,
    },
    {
      title: 'geo:navigation.counseling',
      navId: NavigationIds.BENEFITS,
    },
  ];

  if (geoObjectType.isContinent) {
    return [
      {
        title: 'geo:navigation.travelDestinations',
        navId: NavigationIds.DESTINATIONS,
      },
      {
        title: 'geo:navigation.travels',
        navId: NavigationIds.PRODUCTS,
      },
    ].concat(commonNavigationList);
  }

  return [
    {
      title: 'geo:navigation.travels',
      navId: isB2b ? NavigationIds.TRIPS : NavigationIds.PRODUCTS,
    },
    {
      title: isB2b ? 'geo:navigation.hotelsB2b' : 'geo:navigation.hotelsB2c',
      navId: NavigationIds.HOTELS,
    },
    {
      title: 'geo:navigation.travelDestinations',
      navId: NavigationIds.DESTINATIONS,
    },
  ].concat(commonNavigationList);
};

export function useGeoNavigation(
  geoObjectType: TGeoObjectType,
  geo: FdrGeoContentQuery['fdrGeo'],
  geoModules: TFdrThGeoModuleObject,
  pageComponents: FdrPageComponentsFragment
): TNavigationItem[] {
  const { channelType } = useAuthentication();
  const isB2B = channelType === AuthChannelType.B2B;
  const mapProducts = getFdrGeoMapProducts(geoModules);
  const adventureTravels = getFdrGeoAdventureTravels(geoModules);
  const children = getFdrGeoChildren(geoModules);
  const displayHighlights =
    geo?.highlightsTitle?.length && geo?.highlightsText?.length;
  const navigationItems = getNavigationList(isB2B, geoObjectType);

  return navigationItems.filter(({ navId }) => {
    switch (navId) {
      case NavigationIds.PRODUCTS:
        return adventureTravels?.length;
      case NavigationIds.TRIPS:
        return mapProducts?.length;
      case NavigationIds.DESTINATIONS:
        return children?.page?.length;
      case NavigationIds.HOTELS:
        return geoModules?.hotels?.productsPage?.length;
      case NavigationIds.MEDIA:
        return (
          isGeoProductGalleryAvailable(geoModules?.products) &&
          !displayHighlights
        );
      case NavigationIds.BENEFITS:
        return pageComponents?.components?.find(
          ({ __typename }) => __typename === 'FdrBenefitsComponent'
        );
      case NavigationIds.THEMES:
        return geoModules?.themeGeoPages?.themeGeoPages?.length;
      case NavigationIds.ARTICLES:
        return geoModules?.relatedBlogArticles.blogArticles?.length;
      case NavigationIds.HIGHLIGHTS:
        return displayHighlights;
      case NavigationIds.BEST_TRAVEL_PERIOD:
        return (
          geo?.bestTravelPeriod?.text?.length &&
          geo?.bestTravelPeriod?.title?.length
        );
      default:
        return false;
    }
  });
}

export const handleNavigationClick = (
  id: string,
  headerHeight: number,
  isMobile: boolean
): void => {
  if (!window) return;

  const section = document.getElementById(id);
  const headerOffset = isMobile ? 0 : headerHeight;

  if (section) {
    const sectionPosition = section.offsetTop;
    const offsetPosition = sectionPosition - headerOffset;

    window.scroll({
      top: offsetPosition,
      behavior: 'smooth',
    });

    window.setTimeout(() => {
      if (sectionPosition !== section.offsetTop) {
        window.scroll({
          top: section.offsetTop - headerOffset,
          behavior: 'smooth',
        });
      }
    }, 1000);
  }
};
